/*
 * Dawn v15 header dropdowns: open on mouse hover (desktop only), plus keyboard fixes.
 *
 * Keyboard users get Dawn's own behaviour: Tab moves past a menu without opening it,
 * Enter/Space on the caret toggle opens and closes it. This script adds:
 * - Escape closes the innermost open menu from anywhere, including hover-opened ones,
 *   and returns focus to its toggle if focus was inside it (WCAG 1.4.13, 2.1.1).
 * - Closing a menu also closes any submenus left open inside it.
 * - Submenus close when focus leaves them.
 * - Menus that would run off the right edge of the window open the other way, or are
 *   narrowed to fit, so no link ends up off-screen (WCAG 1.4.10, 2.4.11).
 */
(() => {
  // Desktop header nav only (the header section's id is generated in Dawn 15 section groups)
  const MENU_SELECTOR = '.header__inline-menu details';
  const EDGE_MARGIN = 16;

  function setMenuWidth(menu, px) {
    menu.style.setProperty('width', `${Math.floor(px)}px`, 'important');
    menu.style.setProperty('min-width', `${Math.floor(px)}px`, 'important');
  }

  function keepInViewport(details) {
    const menu = details.querySelector(':scope > .header__submenu');
    if (!menu) return;
    menu.classList.remove('header__submenu--flip');
    menu.style.removeProperty('width');
    menu.style.removeProperty('min-width');

    const rect = menu.getBoundingClientRect();
    const limit = document.documentElement.clientWidth - EDGE_MARGIN;
    if (rect.right <= limit) return;

    const parentMenu = details.parentElement.closest('.header__submenu');
    if (!parentMenu) {
      // Top-level dropdown: right-align it with its menu item instead.
      menu.classList.add('header__submenu--flip');
      return;
    }

    // Flyout: open to the left of the parent dropdown if there is more room there,
    // and narrow it if it still doesn't fit.
    const parentRect = parentMenu.getBoundingClientRect();
    const spaceRight = limit - parentRect.right;
    const spaceLeft = parentRect.left - EDGE_MARGIN;
    if (spaceLeft > spaceRight) {
      menu.classList.add('header__submenu--flip');
      if (rect.width > spaceLeft) setMenuWidth(menu, spaceLeft);
    } else {
      setMenuWidth(menu, spaceRight);
    }
  }

  function setOpen(details, open) {
    if (open) {
      details.setAttribute('open', '');
      keepInViewport(details);
    } else {
      details.removeAttribute('open');
    }
    const summary = details.querySelector(':scope > summary');
    if (summary) summary.setAttribute('aria-expanded', String(open));
  }

  function close(details) {
    details.querySelectorAll('details[open]').forEach((child) => setOpen(child, false));
    setOpen(details, false);
  }

  function bind(root = document) {
    const scope = root.querySelectorAll ? root : document;
    scope.querySelectorAll(MENU_SELECTOR).forEach((details) => {
      if (details.__menuBound) return;
      details.__menuBound = true;

      // Whatever closed this menu (click, Dawn's focusout, Escape), close its submenus too.
      // Menus opened by Dawn (Enter/Space/click) are kept on-screen here.
      details.addEventListener('toggle', () => {
        if (details.open) {
          keepInViewport(details);
        } else {
          close(details);
        }
      });

      // Dawn only closes top-level menus on focusout; do the same for submenus.
      if (details.closest('.header__submenu')) {
        details.addEventListener('focusout', () => {
          setTimeout(() => {
            if (!details.parentElement.contains(document.activeElement)) close(details);
          });
        });
      }

      // Hover covers the whole item (label link + toggle), desktop mouse only.
      const item = details.closest('.header__menu-parent') || details;
      item.addEventListener('mouseenter', () => {
        if (hoverQuery.matches) setOpen(details, true);
      });
      item.addEventListener('mouseleave', () => {
        if (hoverQuery.matches) close(details);
      });
    });
  }

  const hoverQuery = window.matchMedia('(hover: hover) and (pointer: fine)');

  // Escape from anywhere closes the innermost open menu.
  let escapeHandled = false;
  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    const open = document.querySelectorAll(`${MENU_SELECTOR}[open]`);
    if (!open.length) return;

    const innermost = open[open.length - 1];
    const hadFocus = innermost.contains(document.activeElement);
    close(innermost);
    if (hadFocus) innermost.querySelector(':scope > summary')?.focus();
    escapeHandled = true;
  });
  // Dawn's own Escape handler runs on keyup and would close the parent menu as well.
  document.addEventListener(
    'keyup',
    (event) => {
      if (event.key === 'Escape' && escapeHandled) {
        escapeHandled = false;
        event.stopPropagation();
      }
    },
    true
  );

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => bind(document));
  } else {
    bind(document);
  }

  // Re-bind for the theme editor and header re-renders
  document.addEventListener('shopify:section:load', (e) => bind(e.target));
  document.addEventListener('shopify:section:select', (e) => bind(e.target));
  document.addEventListener('shopify:section:reorder', (e) => bind(e.target));
})();
